import React, { FC, useState } from 'react';
import { useFormik } from 'formik';
import PhoneInput, {
  isValidPhoneNumber,
  formatPhoneNumberIntl,
} from 'react-phone-number-input';
import '../../../utils/phoneInputStyle.css';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Col, Row } from 'react-grid-system';
import { useSettings } from '../../../contexts/settingsContext';
import { useReturnContext } from '../../../contexts/returnContext';
import { useTranslation } from '../../../contexts/translationContext';
import { EASYCOM_ROUTES } from '../../../navigation';
import Spacing from '../../atoms/Spacing';
import Typography from '../../atoms/Typography';
import PrimaryButton from '../../molecules/PrimaryButton';
import TextInput from '../../molecules/TextInput';
import { theme } from '../../../utils/theme';
import CheckBox from '../../molecules/Checkbox';
import { useOrderContext } from '../../../contexts/orderContext';
import FormikDropdown from './FormikDropdown';
import { tCountry, tOption } from '../../../contexts/zod';

const StyledErrorMsg = styled(Typography)`
  padding: 0 0 0 8px;
  margin-top: 8px;
  color: ${theme.colors.red[100]};
`;

interface IfieldTable {
  title: string;
  placeholder: string;
  name: string;
  translateKey: string;
  sizes: Record<string, number>;
}

interface Props {
  setPostReturnError: (error: boolean) => void;
}

const fieldTable: IfieldTable[] = [
  {
    title: 'label_first_name',
    placeholder: 'placeholder_first_name',
    name: 'firstName',
    translateKey: 'firstname_field',
    sizes: {
      xs: 12,
      md: 12,
      lg: 6,
    },
  },
  {
    title: 'label_last_name',
    placeholder: 'placeholder_last_name',
    name: 'lastName',
    translateKey: 'lastname_field',
    sizes: {
      xs: 12,
      md: 12,
      lg: 6,
    },
  },
  {
    title: 'your_email',
    placeholder: 'placeholder_email_field',
    name: 'email',
    translateKey: 'email_field',
    sizes: {
      xs: 12,
      md: 12,
      lg: 12,
    },
  },
];

const addressFields: IfieldTable[] = [
  {
    title: 'addressline_1',
    placeholder: 'placeholder_addressline_1',
    name: 'addressLine1',
    translateKey: 'addressline_1',
    sizes: {
      xs: 12,
      md: 12,
      lg: 12,
    },
  },
  {
    title: 'addressline_2',
    placeholder: 'placeholder_addressline_2',
    name: 'addressLine2',
    translateKey: 'addressline_2',
    sizes: {
      xs: 12,
      md: 12,
      lg: 12,
    },
  },
  {
    title: 'addressline_3',
    placeholder: 'placeholder_addressline_3',
    name: 'addressLine3',
    translateKey: 'addressline_3',
    sizes: {
      xs: 12,
      md: 12,
      lg: 12,
    },
  },
  {
    title: 'addressline_4',
    placeholder: 'placeholder_addressline_4',
    name: 'addressLine4',
    translateKey: 'addressline_4',
    sizes: {
      xs: 12,
      md: 12,
      lg: 12,
    },
  },
  {
    title: 'city',
    placeholder: 'placeholder_city',
    name: 'city',
    translateKey: 'city',
    sizes: {
      xs: 12,
      md: 12,
      lg: 12,
    },
  },
  {
    title: 'region',
    placeholder: 'placeholder_region',
    name: 'region',
    translateKey: 'region',
    sizes: {
      xs: 12,
      md: 12,
      lg: 12,
    },
  },
];

const ShippingForm: FC<Props> = ({ setPostReturnError }) => {
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { cornerRadius } = useSettings();
  const {
    setShippingContactInfo,
    loading: loadingSendingReturns,
    postReturn,
    setReturns,
    returns,
    pickupContactInfo,
    shippingContactInfo,
  } = useReturnContext();

  const { defaultShippingContact } = useOrderContext();
  const { shippingFormEnabled, countries } = useSettings();
  const [useSalesOrderAddress, setUseSalesOrderAddress] = useState(true);

  if (!shippingFormEnabled || !Object.values(returns).some((x) => x.returnData.isClaim)) {
    navigate(EASYCOM_ROUTES.RETURNS_SUMMARY);
  }

  const countryOptions = countries.map((x) => ({
      value: x.code,
      label: x.name,
    }));

  const initialValues = {
    firstName: shippingContactInfo?.firstName || pickupContactInfo?.firstName || '',
    lastName: shippingContactInfo?.lastName || pickupContactInfo?.lastName || '',
    email: shippingContactInfo?.email || pickupContactInfo?.email || '',
    phoneNumber: shippingContactInfo?.phoneNumber || pickupContactInfo?.phoneNumber || '',
    addressLine1: shippingContactInfo?.addressLine1 || defaultShippingContact?.addressLine1 || '',
    addressLine2: shippingContactInfo?.addressLine2 || defaultShippingContact?.addressLine2 || '',
    addressLine3: shippingContactInfo?.addressLine3 || defaultShippingContact?.addressLine3 || '',
    addressLine4: shippingContactInfo?.addressLine4 || defaultShippingContact?.addressLine4 || '',
    city: shippingContactInfo?.city || defaultShippingContact?.city || '',
    district: shippingContactInfo?.district || defaultShippingContact?.district || '',
    region: shippingContactInfo?.region || defaultShippingContact?.region || '',
    postalCode: shippingContactInfo?.postalCode || defaultShippingContact?.postalCode || '',
    country: shippingContactInfo?.country || defaultShippingContact?.country || '',
  };

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .when([], {
        is: () => useSalesOrderAddress,
        then: (v) => v.notRequired(),
        otherwise: (v) => v.required(
          `${translate('placeholder_first_name', null)} ${translate(
            'is_required',
            null,
          )}`,
        ),
      }),
    lastName: Yup.string()
      .when([], {
        is: () => useSalesOrderAddress,
        then: (v) => v.notRequired(),
        otherwise: (v) => v.required(
          `${translate('placeholder_last_name', null)} ${translate(
            'is_required',
            null,
          )}`,
        ),
      }),
    email: Yup.string()
      .when([], {
        is: () => useSalesOrderAddress,
        then: (v) => v.notRequired(),
        otherwise: (v) => v.required(`${translate('email', null)} ${translate('is_required', null)}`)
          .email(translate('must_be_valid_email', null)),
      }),
    phoneNumber: Yup.string()
      .when([], {
        is: () => useSalesOrderAddress,
        then: (v) => v.notRequired(),
        otherwise: (v) => v.required(`${translate('phone', null)} ${translate('is_required', null)}`),
      }).test(
        'customPhoneNumberValidation',
        translate('invalid_phone_number', null),
        // eslint-disable-next-line func-names
        function (value) {
          const { path, createError } = this;
          if (!useSalesOrderAddress && (!value || !isValidPhoneNumber(value))) {
            return createError({
              path,
              message: translate('invalid_phone_number', null),
            });
          }
          return true;
        },
      ),
    addressLine1: Yup.string()
      .when([], {
        is: () => useSalesOrderAddress,
        then: (v) => v.notRequired(),
        otherwise: (v) => v.required(`${translate('addressline_1', null)} ${translate('is_required', null)}`),
      }),
    city: Yup.string()
      .when([], {
        is: () => useSalesOrderAddress,
        then: (v) => v.notRequired(),
        otherwise: (v) => v.required(`${translate('city', null)} ${translate('is_required', null)}`),
      }),
    postalCode: Yup.string()
      .when([], {
        is: () => useSalesOrderAddress,
        then: (v) => v.notRequired(),
        otherwise: (v) => v.required(`${translate('postal_code', null)} ${translate('is_required', null)}`),
      }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const newContactInfo = {
        ...values,
        phoneNumber: formatPhoneNumberIntl(values.phoneNumber),
      };
      setShippingContactInfo(newContactInfo);

      const result = await postReturn(
        pickupContactInfo,
        true,
        useSalesOrderAddress
          ? null
          : newContactInfo,
      );

      if (result.status === 'Failure') {
        setPostReturnError(true);
      } else {
        setReturns({});
        navigate(EASYCOM_ROUTES.RETURNS_COMPLETE);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>

      <div style={{ cursor: 'default' }}>
        <Col style={{ width: 'fit-content' }} onClick={() => setUseSalesOrderAddress(!useSalesOrderAddress)}>
          <CheckBox checked={useSalesOrderAddress} labelKey="same_as_pickup" borderRadius={cornerRadius} />
        </Col>
      </div>
      <Spacing spacing={3} />
      <Row>
        <Col xs={12} md={12} lg={8}>
          <Typography
            align="left"
            translateKey="enter_shipping_details"
            variant="h4"
          />

          <Spacing spacing={3} />
          <Row gutterWidth={16}>
            {fieldTable?.map((field) => (
              <Col
                key={field.name}
                xs={field.sizes.xs}
                md={field.sizes.md}
                lg={field.sizes.lg}
              >
                <TextInput
                  disabled={useSalesOrderAddress}
                  title={field.title}
                  placeholder={field.placeholder}
                  name={field.name}
                  translateKey={field.translateKey}
                  value={formik.values[field.name]}
                  error={
                    formik.touched[field.name] && formik.errors[field.name]
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    formik.setFieldValue(field.name, e.target.value)
                  }
                />
                <Spacing spacing={3} />
              </Col>
            ))}
          </Row>
          <Typography translateKey="phone_field" variant="input-label" />
          <PhoneInput
            disabled={useSalesOrderAddress}
            style={{ paddingLeft: 8 }}
            placeholder={translate('placeholder_phone_field', null)}
            defaultCountry="SE"
            value={formik.values.phoneNumber}
            name="phone"
            onChange={(phoneNumber) =>
              formik.setFieldValue('phoneNumber', phoneNumber)
            }
          />
          {formik.touched.phoneNumber && formik.errors.phoneNumber && (
            <StyledErrorMsg variant="input-label">
              {formik.errors.phoneNumber}
            </StyledErrorMsg>
          )}

          <Spacing spacing={3} />

          <Row gutterWidth={16}>
            {addressFields?.map((field) => (
              <Col
                key={field.name}
                xs={field.sizes.xs}
                md={field.sizes.md}
                lg={field.sizes.lg}
              >
                <TextInput
                  disabled={useSalesOrderAddress}
                  title={field.title}
                  placeholder={field.placeholder}
                  name={field.name}
                  translateKey={field.translateKey}
                  value={formik.values[field.name]}
                  error={
                    formik.touched[field.name] && formik.errors[field.name]
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    formik.setFieldValue(field.name, e.target.value)
                  }
                />
                <Spacing spacing={3} />
              </Col>
            ))}
          </Row>
          <Row gutterWidth={16}>
            <Col
              xs={6}
              md={3}
            >
              <FormikDropdown
                name="country"
                error={formik.touched.country && formik.errors.country}
                translateKey="country_placeholder"
                options={countryOptions}
                onChange={(data: tOption) => {
                  formik.setFieldValue('country', data.value);
                }}
                value={formik.values.country}
                className={undefined}
                disabled={useSalesOrderAddress}
              />
            </Col>
            <Col
              xs={6}
              md={9}
            >
              <TextInput
                disabled={useSalesOrderAddress}
                title="postal_code"
                placeholder="placeholder_postal_code"
                name="postalCode"
                translateKey="postal_code"
                value={formik.values.postalCode}
                error={
                  formik.touched.postalCode && formik.errors.postalCode
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  formik.setFieldValue('postalCode', e.target.value)
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Col xs={12} md={12} lg={8}>
        <Spacing spacing={3} />
        <PrimaryButton
          disabled={loadingSendingReturns}
          loading={loadingSendingReturns}
          translateKey="send_returns"
          icon="send-package"
          type="submit"
        />
        <Spacing spacing={4} />
      </Col>
    </form>
  );
};

export default ShippingForm;
